<template>
  <div>
    <v-container
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        :size="100"
        color="#2661FF"
        indeterminate
      ></v-progress-circular>
    </v-container>
    <v-container v-else class="px-5 my-5">
      <h4 class="title">{{ $t("leads.title") }}</h4>
      <v-breadcrumbs
        divider=">"
        :items="breadcrumbItems"
        class="pa-0 mt-2"
      ></v-breadcrumbs>
      <!------------------------------------------------->
      <!-- DATE FILTER --------------------------------------->
      <!------------------------------------------------->
      <v-row no-gutters>
        <v-col cols="12" class="d-flex justify-end">
          <DateBar
            :dateRange="date.dateRange"
            @updateRange="
              (value, startDate, index) => {
                date.dateRange = index;
                endDate = $moment().format('YYYY-MM-DD');
                changeDates(startDate, endDate);
              }
            "
            format-date="YYYY-MM-DD"
          />
          <DateFilters
            :initialDate="date.startDate"
            :finalDate="date.endDate"
            @updateInitialDate="updateDates(date.startDate, $event)"
            @updateFinalDate="updateDates(date.endDate, $event)"
          />
        </v-col>
      </v-row>
      <!------------------------------------------------->
      <!-- TABLE --------------------------------------->
      <!------------------------------------------------->
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg pa-4 card" flat>
            <v-card-title>
              <!------------------------------------------------->
              <!-- FILTERS --------------------------------------->
              <!------------------------------------------------->
              <v-row no-gutters>
                <v-col cols="12" md="3" class="px-2">
                  <v-text-field
                    ref="button-search"
                    v-model="leadsTable.filters.search"
                    :label="$t('general.search')"
                    outlined
                    dense
                    clearable
                    append-icon="search"
                    hide-details
                    class="rounded-lg"
                    @click:append="fetchLeads()"
                    @keyup.enter="fetchLeads()"
                    @click:clear="clearSearch()"
                  />
                </v-col>
                <v-col cols="12" md="3" class="px-2">
                  <v-select
                    v-model="leadsTable.filters.status"
                    :items="leadStatuses"
                    item-text="name"
                    item-value="id"
                    :label="$t('leads.headers.status')"
                    outlined
                    dense
                    clearable
                    hide-details
                    class="rounded-lg"
                    @change="fetchLeads()"
                  />
                </v-col>
                <!------------------------------------------------->
                <!-- EXPORTS --------------------------------------->
                <!------------------------------------------------->
                <v-col
                  v-if="exportsAllowed()"
                  cols="12"
                  md="6"
                  class="d-flex justify-end"
                >
                  <v-btn
                    color="#2661FF"
                    class="rounded-lg"
                    @click="toggleDialogExport(true)"
                  >
                    <v-icon color="white" class="mr-2">cloud_download</v-icon>
                    <span class="white--text">
                      {{ $t("general.export") }}
                    </span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    ref="leads-data-table"
                    :loading="leadsTable.isLoading"
                    :headers="leadsTable.headers"
                    :items="leadsTable.items"
                    :server-items-length="leadsTable.total"
                    :items-per-page="25"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 25, 50, 100]
                    }"
                    :no-data-text="$t('leads.no_data')"
                    :no-results-text="$t('leads.no_data')"
                    @update:options="leadsTable.pagination = $event"
                    @click:row="item => open(item)"
                    class="row-cursor-pointer"
                  >
                    <template v-slot:item.first_name="{ item }">
                      <div class="v-list--dense text-no-wrap">
                        <v-list-item class="pa-0">
                          <v-list-item-avatar
                            :color="randomizedColor()"
                            class="mr-4"
                            size="30"
                          >
                            <span class="white--text caption text-uppercase">
                              {{
                                getInitials(
                                  item.first_name,
                                  item.last_name,
                                  item.email
                                )
                              }}
                            </span>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-medium">
                              {{
                                `${item.first_name || ""} ${item.last_name ||
                                  ""}`
                              }}
                            </v-list-item-title>
                            <v-list-item-subtitle
                              class="grey--text text--darken-2"
                            >
                              {{ item.email }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </div>
                    </template>
                    <template v-slot:item.status="{ item }">
                      <div class="text-no-wrap">
                        <v-icon
                          :color="getStatus(item.status).color"
                          size="18"
                          left
                        >
                          {{ getStatus(item.status).icon }}
                        </v-icon>
                        {{ getStatus(item.status).text }}
                      </div>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      <div>
                        {{ item.created_at | moment("LLL") }}
                      </div>
                    </template>
                    <template v-slot:item.phone="{ item }">
                      <div>
                        {{ item.phone ? item.phone : "-" }}
                      </div>
                    </template>
                    <template v-slot:item.email_status="{ item }">
                      <v-tooltip left color="black">
                        <template v-slot:activator="{ on, attrs }">
                          <div class="d-flex justify-center">
                            <v-icon
                              :color="
                                getColorTypeStatusEmail(item.email_status)
                              "
                              v-bind="attrs"
                              v-on="on"
                              size="15"
                              small
                            >
                              {{ getIconTypeStatusEmail(item.email_status) }}
                            </v-icon>
                          </div>
                        </template>
                        <span>{{ getTextStatusEmail(item.email_status) }}</span>
                      </v-tooltip>
                    </template>
                    <template v-slot:item.country="{ item }">
                      <span>
                        {{ item.country ? item.country.name : "-" }}
                      </span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                            @click="open(item)"
                          >
                            <v-icon>
                              visibility
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("general.view") }}</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <DialogExport
        :module-options="['DRBLeads']"
        :startDate="date.startDate"
        :endDate="date.endDate"
        :filters="filters"
      />
      <DialogMessage />
      <LeadDetailDrawer v-if="isOpenLeadDrawer" />
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import DateFilters from "@/components/Common/DateFilters";
import DateBar from "@/components/Common/DateBar";
import leadMixin from "@/mixins/leads";
import DialogExport from "@/components/Downloads/DialogExport";
import DialogMessage from "@/components/Downloads/DialogMessage";
import LeadDetailDrawer from "@/components/Leads/Details/LeadDetailDrawer";

export default {
  name: "Leads",
  components: {
    DateFilters,
    DateBar,
    DialogExport,
    DialogMessage,
    LeadDetailDrawer
  },
  mixins: [leadMixin],
  data() {
    return {
      isLoading: false,
      breadcrumbItems: [
        {
          text: this.$t("leads.title"),
          disabled: false
        },
        {
          text: "index",
          disabled: true
        }
      ],
      date: {
        startDate: this.$moment()
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: this.$moment()
          .endOf("day")
          .format("YYYY-MM-DD"),
        dateRange: 3
      },
      leadsTable: {
        isLoading: false,
        items: [],
        total: 0,
        filters: {
          search: null,
          status: null
        },
        pagination: {
          sortBy: ["created_at"],
          sortDesc: [true],
          itemsPerPage: 25
        },
        headers: [
          {
            text: "ID",
            value: "id",
            align: "start",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.name"),
            value: "first_name",
            align: "start",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.status"),
            value: "status",
            align: "start",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.phone"),
            value: "phone",
            align: "center",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.created_at"),
            value: "created_at",
            align: "center",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.email"),
            value: "email_status",
            align: "start",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.country"),
            value: "country",
            align: "start",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("leads.headers.actions"),
            value: "actions",
            align: "center",
            sortable: false,
            class: "header_color"
          }
        ]
      },
      leadStatuses: [
        {
          id: this.getLeadStatusValueByName("lead"),
          name: this.$t("leads.statuses.lead")
        },
        {
          id: this.getLeadStatusValueByName("donor"),
          name: this.$t("leads.statuses.donnor")
        },
        {
          id: this.getLeadStatusValueByName("recurrent"),
          name: this.$t("leads.statuses.recurrent")
        }
      ]
    };
  },
  async created() {
    await this.getCurrencies();
  },
  watch: {
    "leadsTable.pagination": {
      deep: true,
      async handler() {
        await this.fetchLeads();
      }
    }
  },
  computed: {
    ...mapState({
      leads: ({ leads: { leads } }) => leads,
      totalLeads: ({ leads: { totalLeads } }) => totalLeads,
      isOpenLeadDrawer: ({ leads: { isOpenLeadDrawer } }) => isOpenLeadDrawer
    }),
    filters() {
      const { filters } = this.leadsTable;

      return {
        search: filters.search,
        status: filters.status
      };
    }
  },
  methods: {
    ...mapActions({
      getLeads: "leads/getLeads",
      openLeadDetailsDrawer: "leads/openLeadDrawer",
      getCurrencies: "countries/getCurrencies"
    }),
    ...mapMutations({
      toggleDialogExport: "downloads/toggleDialogExport"
    }),
    async updateDates(property, date) {
      this.date[property] = date;
      this.date.dateRange = 7;
      this.changeDates(this.date.startDate, this.date.endDate);
    },
    changeDates(initialDate, finalDate) {
      this.date.startDate = !initialDate ? this.date.startDate : initialDate;
      this.date.endDate = !finalDate ? this.date.endDate : finalDate;
      this.fetchLeads();
    },
    async fetchLeads(startDate, endDate) {
      const { leadsTable } = this;
      const { pagination, filters } = leadsTable;

      const { page, itemsPerPage: rowsPerPage } = pagination;
      let { sortBy, sortDesc: descending } = pagination;
      const { search, status } = filters;

      const initialDate = !startDate ? this.date.startDate : startDate;
      const finalDate = !endDate ? this.date.endDate : endDate;

      if (Array.isArray(sortBy)) {
        [sortBy] = sortBy;
      }
      if (Array.isArray(descending)) {
        [descending] = descending;
      }

      leadsTable.isLoading = true;

      const payload = {
        startDate: initialDate,
        endDate: finalDate,
        page,
        rowsPerPage,
        sortBy,
        descending,
        search,
        status
      };

      await this.getLeads(payload);

      leadsTable.items = this.leads;
      leadsTable.total = Number(this.totalLeads);

      leadsTable.isLoading = false;
    },
    async clearSearch() {
      this.leadsTable.filters.search = null;
      await this.fetchLeads();
    },
    randomizedColor() {
      const letters = "0123456789ABCDEF";
      let color = "#";
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    async open(item) {
      await this.openLeadDetailsDrawer({ leadId: item.id });
    },
    exportsAllowed() {
      return this.leadsTable.items.length > 0;
    }
  }
};
</script>

<style>
.card {
  border: 1px solid #d0d5dd !important;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.15) !important;
}

.header_color {
  color: #2661ff !important;
}

.row-cursor-pointer tbody tr {
  cursor: pointer !important;
}
</style>
